<script setup lang="ts">
// Two access states with a stable identity: a signed-in account that isn't a
// member of this workspace, and a member whose role doesn't open this page.
import { computed } from "vue";
import { useRoute } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import BrandLockup from "@/components/ui/BrandLockup.vue";
import Icon from "@/components/ui/Icon.vue";

const route = useRoute();
const auth = useAuthStore();
const forbidden = computed(() => route.meta.forbidden === true);
</script>

<template>
  <div class="access">
    <BrandLockup />
    <section class="panel card" aria-labelledby="access-h">
      <div class="state-symbol"><Icon :name="forbidden ? 'lock' : 'user'" /></div>
      <span class="eyebrow">Organisation access</span>
      <template v-if="forbidden">
        <h1 id="access-h">This page needs another role.</h1>
        <p>You're signed in as <strong>{{ auth.email }}</strong> ({{ auth.role }}). An organisation admin can change your role if you need this page.</p>
        <div class="row">
          <RouterLink to="/" class="btn btn--primary"><Icon name="back" />Back to Mission Control</RouterLink>
        </div>
      </template>
      <template v-else>
        <h1 id="access-h">Right place. Different account.</h1>
        <p><strong>{{ auth.email || "This account" }}</strong> isn't a member of this organisation. Sign in with your invited work account, or ask an admin to invite this one.</p>
        <div class="row">
          <button class="btn btn--primary" @click="auth.signOut()"><Icon name="logout" />Use another account</button>
        </div>
      </template>
    </section>
  </div>
</template>

<style scoped>
.access { min-height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: var(--space-6); padding: var(--space-6) var(--space-5); }
.card { max-width: 520px; width: 100%; padding: var(--space-7); }
.state-symbol { width: 50px; height: 50px; border-radius: var(--radius-m); display: grid; place-items: center; border: 1px solid var(--line); background: var(--bg-sunk); color: var(--brand); margin-bottom: var(--space-5); }
h1 { font-size: var(--text-xl); font-weight: 600; letter-spacing: -.5px; margin: var(--space-3) 0 var(--space-3); }
p { color: var(--muted); font-size: var(--text-sm); margin-bottom: var(--space-5); }
p strong { color: var(--ink); font-weight: 500; }
</style>
